import type React from "react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { client } from "@/api-client";
import type { NotificationOutput } from "@/api-types";
import { useUser } from "@/UserContext";
import { useWebSocket } from "@/websocket/client";

interface NotificationsContextType {
  notifications: NotificationOutput[];
  unreadCount: number;
  isLoading: boolean;
  refetch: () => void;
  deleteNotifications: (ids: string[]) => Promise<void>;
  markAsRead: (ids: "all" | [string, ...string[]]) => Promise<void>;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationOutput[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { lastMessage } = useWebSocket();

  const userInfo = useUser();
  const { data, refetch } = client.useQuery(
    "get",
    "/notifications/",
    {
      params: {
        query: {},
      },
    },
    {
      staleTime: 3600,
      enabled: userInfo.isProfileCreated,
    },
  );

  // Initialize notifications from API response
  useEffect(() => {
    if (data) {
      setNotifications((data.notifications as NotificationOutput[]) || []);
      setIsLoading(false);
    }
  }, [data]);

  // Handle incoming websocket notifications
  useEffect(() => {
    if (!(lastMessage && lastMessage.type === "notification")) {
      return;
    }
    const newNotification = lastMessage.notification;

    // Add new notification if it doesn't exist
    setNotifications((prev) => {
      const prevWithoutCurrent = prev.filter(
        (n) => n.id !== newNotification.id,
      );
      return [newNotification, ...prevWithoutCurrent].sort((a, b) =>
        a.createdAt > b.createdAt ? -1 : 1,
      );
    });
  }, [lastMessage]);

  // Mutation for deleting notifications
  const deleteNotifications = client.useMutation(
    "post",
    "/notifications/delete",
  );

  // Function to delete notifications
  const handleDelete = useCallback(
    async (notificationIds: string[]) => {
      await deleteNotifications.mutateAsync({
        body: {
          notificationIds,
        },
      });

      // Remove the deleted notifications from state
      setNotifications((prev) =>
        prev.filter(
          (notification) => !notificationIds.includes(notification.id),
        ),
      );
    },
    [deleteNotifications],
  );

  // Mutation for marking all notifications as read
  const { mutateAsync: markAllAsReadMutate } = client.useMutation(
    "post",
    "/notifications/mark-all-as-read",
  );

  const { mutateAsync: markAsReadMutate } = client.useMutation(
    "post",
    "/notifications/mark-as-read",
  );

  const markAsRead = useCallback(
    async (notificationIds: "all" | [string, ...string[]]) => {
      if (notificationIds === "all") {
        await markAllAsReadMutate({});

        // Update all notifications in state
        setNotifications((prev) =>
          prev.map((notification) => ({ ...notification, isRead: true })),
        );
        return;
      }

      await markAsReadMutate({
        body: {
          notificationIds,
        },
      });
      // Update all notifications in state
      setNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          isRead: notificationIds.includes(notification.id)
            ? true
            : notification.isRead,
        })),
      );
    },
    [markAsReadMutate, markAllAsReadMutate],
  );

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const value = {
    notifications,
    unreadCount,
    isLoading,
    refetch,
    deleteNotifications: handleDelete,
    markAsRead,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider",
    );
  }
  return context;
};

export const useUnreadNotificationsCount = () => {
  const { unreadCount, refetch } = useNotifications();
  return { unreadCount, refetch };
};
